/**
 * chartSignals — the signal feed's list: the bot's signals (server, using
 * the bot's UT Bot settings) plus, for every symbol + timeframe that has a
 * chart loaded, the same bot settings run on that chart's candles. That part
 * updates the moment the bot's KEY VAL / ATR PERIOD change, instead of
 * waiting for the server's next new signal. Charts' own settings only drive
 * their arrows and play no part here. No React / Zustand dependencies.
 */

import type { Candle, ChartPanel, TimeframeKey, UTBotSignal } from '@arc/shared';
import { DEFAULT_UTBOT_CONFIG, isClosedCandle, resolveUTBotSettings, runUTBot, withConfirmations } from '@arc/shared';

export const FEED_SIGNAL_LIMIT = 200;

/** Same identity for a signal however many times it's computed or re-sent. */
export function signalKey(s: UTBotSignal): string {
  return `${s.symbol}:${s.timeframe}:${s.timestamp}:${s.type}`;
}

type PanelLike = Pick<ChartPanel, 'symbol' | 'timeframe'>;
type BotSettings = { keyValue: unknown; atrPeriod: unknown };

export function mergeFeedSignals(
  serverSignals: UTBotSignal[],
  panels: PanelLike[],
  candles: Record<string, Candle[]>,
  bot: BotSettings,
  limit = FEED_SIGNAL_LIMIT,
  nowMs = Date.now(),
): UTBotSignal[] {
  const settings = resolveUTBotSettings(bot.keyValue, bot.atrPeriod, DEFAULT_UTBOT_CONFIG);
  const charted = new Set<string>();
  const fromCharts: UTBotSignal[] = [];
  for (const p of panels) {
    const key = `${p.symbol}:${p.timeframe}`;
    const cs  = candles[key];
    if (charted.has(key) || !cs || cs.length === 0) continue;
    charted.add(key);
    const config = { ...settings, timeframe: p.timeframe as TimeframeKey };
    // A signal on the still-forming candle is shown, flagged provisional:
    // the bot only trades signals on closed candles.
    fromCharts.push(...withConfirmations(cs, runUTBot(cs, config, p.symbol).signals).map(s =>
      isClosedCandle(s.timestamp, s.timeframe, nowMs) ? s : { ...s, provisional: true }));
  }

  const seen = new Set<string>();
  return [...fromCharts, ...serverSignals.filter(s => !charted.has(`${s.symbol}:${s.timeframe}`))]
    .filter(s => { const k = signalKey(s); if (seen.has(k)) return false; seen.add(k); return true; })
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, limit);
}
